import React from "react";

import MUIMenuItem from "@mui/material/MenuItem";
import MUIListItemIcon from "@mui/material/ListItemIcon";
import MUIFileDownloadIcon from "@mui/icons-material/FileDownload";
import MUISelectAllIcon from "@mui/icons-material/SelectAll";
import MUIDeselectIcon from "@mui/icons-material/Deselect";
import MUIMoveDownIcon from "@mui/icons-material/MoveDown";
import MUICheckBoxIcon from "@mui/icons-material/CheckBox";
import MUICheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import MUIBox from "@mui/material/Box";
import DialogConfirmMoveAccounts from "../../Dialog/DialogConfirmMoveAccounts";

import CardClass from "../../CardClass";
import Progress from "../../Progress";
import EmptyState from "../../EmptyState";
import Menu from "../../Menu";
import Exporter from "../../../Utility/Exporter";
import DataGrid from "../../DataGrid";

export default class CardMyAccountsResult extends CardClass {
  constructor(props) {
    super(props);

    this.state = {
      allAccountsSelected: false,
      openMergeAccountsDialog: false,
      openMoveAccountsDialog: false,
      currentSelectedRow: [],
      filteredRows: [],
      sortEmptyToBottom: true,
    };
  }
  componentDidUpdate(prevProps) {
    if (prevProps.selectedAccounts !== this.props.selectedAccounts) {
      this.setState({ selectedAccounts: this.props.selectedAccounts });
    }
  }

  handleFilteredRowsChange = (filteredRows) => {
    this.props.selectedAccountsChange({});
    this.setState({ filteredRows, allAccountsSelected: false });
  };

  getSortingColumnExtensions() {
    if (!this.state.sortEmptyToBottom) {
      return [];
    }

    const getSortDirection = (columnName) => {
      const sorting = this.props.sorting || [];
      const sortItem = sorting.find((item) => item.columnName === columnName);
      return sortItem ? sortItem.direction : "asc";
    };

    const comparePrimitive = (a, b) => {
      if (a < b) return -1;
      if (a > b) return 1;
      return 0;
    };

    const isEmpty = (value) =>
      value === null || value === undefined || value === "";

    const nullLastSort = (columnName) => ({
      columnName: columnName,
      compare: (a, b) => {
        const isDesc = getSortDirection(columnName) === "desc";
        const aIsEmpty = isEmpty(a);
        const bIsEmpty = isEmpty(b);

        if (aIsEmpty && bIsEmpty) return 0;
        // DevExpress negates compare output for desc.
        if (aIsEmpty) return isDesc ? -1 : 1;
        if (bIsEmpty) return isDesc ? 1 : -1;

        return comparePrimitive(a, b);
      },
    });

    return [
      nullLastSort("daysSinceLastInService"),
      nullLastSort("lastCalledOnDate"),
      nullLastSort("lastInServiceDate"),
    ];
  }

  getActions() {
    return [
      <Menu
        key="export"
        iconButtonSize="small"
        menuItems={[
          this.props.selectedAccounts &&
          Object.values(this.props.selectedAccounts).length > 0 ? (
            <MUIMenuItem
              key="deselectAllAccounts"
              disabled={this.props.isLoading || !this.props.rows?.length}
              onClick={() => {
                this.setState({ allAccountsSelected: false });
                this.props.selectedAccountsChange({});
              }}
            >
              <MUIListItemIcon>
                <MUIDeselectIcon fontSize="small" />
              </MUIListItemIcon>
              {`Deselect All`}
            </MUIMenuItem>
          ) : (
            <MUIMenuItem
              key="selectAllAccounts"
              disabled={this.props.isLoading || !this.props.rows?.length}
              onClick={() => {
                const allSelected = !this.state.allAccountsSelected;
                const newSelected = allSelected ? this.state.filteredRows : {};
                this.setState({ allAccountsSelected: allSelected });
                this.props.selectedAccountsChange(newSelected);
              }}
            >
              <MUIListItemIcon>
                <MUISelectAllIcon fontSize="small" />
              </MUIListItemIcon>
              Select All
            </MUIMenuItem>
          ),

          this.state.sortEmptyToBottom ? (
            <MUIMenuItem
              key="sortEmptyToBottomOn"
              onClick={() => {
                this.setState({ sortEmptyToBottom: false });
              }}
            >
              <MUIListItemIcon>
                <MUICheckBoxIcon fontSize="small" />
              </MUIListItemIcon>
              Sort Empty Values To Bottom
            </MUIMenuItem>
          ) : (
            <MUIMenuItem
              key="sortEmptyToBottomOff"
              onClick={() => {
                this.setState({ sortEmptyToBottom: true });
              }}
            >
              <MUIListItemIcon>
                <MUICheckBoxOutlineBlankIcon fontSize="small" />
              </MUIListItemIcon>
              Sort Empty Values To Bottom
            </MUIMenuItem>
          ),
          <MUIMenuItem
            key="moveAccount"
            disabled={
              this.props.isLoading ||
              !this.props.rows?.length ||
              Object.values(this.props.selectedAccounts).length === 0
            }
            onClick={() => {
              this.setState({ openMoveAccountsDialog: true });
            }}
          >
            <MUIListItemIcon>
              <MUIMoveDownIcon fontSize="small" />
            </MUIListItemIcon>
            {`Move Selected`}
          </MUIMenuItem>,
          <MUIMenuItem
            key="csv"
            disabled={this.props.isLoading || !this.props.rows?.length}
            onClick={() => {
              const headers = this.props.columns.map((c) => c.title);
              const keys = this.props.columns.map((c) => c.name);
              const csvRows = [
                headers,
                ...this.props.rows.map((row) =>
                  keys.map((key) => {
                    const value = row[key];
                    return value == null ? "" : value;
                  }),
                ),
              ];
              Exporter.exportCSV(csvRows, this.props.exportFilename);
            }}
          >
            <MUIListItemIcon>
              <MUIFileDownloadIcon fontSize="small" />
            </MUIListItemIcon>
            Export CSV
          </MUIMenuItem>,
        ]}
      />,
    ];
  }

  renderContent() {
    if (this.props.isLoading) {
      return <Progress message="Loading Data..." />;
    }
    if (!this.props.rows?.length) {
      return (
        <EmptyState
          line1={
            this.props.loadedAt ? "No results found" : "Waiting for results"
          }
          line2={
            this.props.loadedAt
              ? "Try adjusting your settings"
              : "Please select your settings"
          }
        />
      );
    }

    return (
      <MUIBox>
        <DataGrid
          rows={this.props.rows}
          sorting={
            this.props.sorting?.length
              ? this.props.sorting
              : [{ columnName: "points", direction: "desc" }]
          }
          sortingColumnExtensions={this.getSortingColumnExtensions()}
          onSortingChange={this.props.onSortingChange}
          onFilteredRowsChange={this.handleFilteredRowsChange.bind(this)}
          columns={this.props.columns}
          searchColumns={["account"]}
          defaultColumnWidths={this.props.defaultColumnWidths}
          selectedItems={this.props.selectedAccounts}
          enableGroupingPanel={true}
          onGroupingChange={this.props.onGroupingChange}
          onClick={(row) => window.open(`/account/${row.id}/detail`, "_blank")}
          onClickRowAction={(rowName, row) => {
            const exists = this.props.selectedAccounts[row.id] !== undefined;
            let newSelected;
            if (exists) {
              newSelected = { ...this.props.selectedAccounts };
              delete newSelected[row.id];
            } else {
              newSelected = { ...this.props.selectedAccounts, [row.id]: row };
            }
            this.props.selectedAccountsChange(newSelected);
          }}
          grouping={this.props.grouping}
          groupSummaryItems={this.props.groupSummaryItems}
        />
        {this.state.openMoveAccountsDialog && (
          <DialogConfirmMoveAccounts
            open={this.state.openMoveAccountsDialog}
            selectedAccounts={this.props.selectedAccounts}
            onClose={(accounts, runReportAfterUpdate) =>
              this.setOpenMoveAccountsDialog(accounts, runReportAfterUpdate)
            }
          />
        )}
        {/* <Menu
                    key="account-performance-report-menu"
                    menuItems={[
                        <MUIMenuItem
                            key="account-performance-report"
                            disabled={this.props.isLoading || !this.props.rows?.length}
                            onClick={() => {
                                console.log("Dialog to task sales rep");
                            }}
                        >
                            <MUIListItemIcon>
                                <MUIPersonAddIcon fontSize="small" />
                            </MUIListItemIcon>
                            Task Sales Rep
                        </MUIMenuItem>
                    ]}
                /> */}
      </MUIBox>
    );
  }

  setOpenMoveAccountsDialog(accounts, runReportAfterUpdate) {
    this.setState({
      allAccountsSelected: false,
      openMoveAccountsDialog: false,
    });
    this.props.selectedAccountsChange(accounts || {});
    this.props.runReportAfterUpdate(runReportAfterUpdate);
  }

  getTitle() {
    return "Results";
  }
}
